import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

export type PropUnitStatus = 'ok' | 'in_repair' | 'retired';

export interface PropUnit {
  id: string;
  propItemId: string;
  serial: number;
  version: string | null;
  publicToken: string;
  status: PropUnitStatus;
  note: string | null;
  createdAt: string;
  _count: { repairs: number };
}

export interface RepairLog {
  id: string;
  description: string;
  performedBy: string | null;
  cost: string | null;
  createdAt: string;
}

const KEY = (propItemId: string) => ['prop-units', propItemId] as const;

export function usePropUnits(propItemId: string) {
  return useQuery({ queryKey: KEY(propItemId), queryFn: () => api.get<PropUnit[]>(`/api/prop-items/${propItemId}/units`) });
}

function useUnitMutation<T>(propItemId: string, fn: (v: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(propItemId) });
      qc.invalidateQueries({ queryKey: ['prop-items', propItemId] });
    },
  });
}

export const useGenerateUnits = (propItemId: string) =>
  useUnitMutation(propItemId, (p: { count: number; version?: string }) => api.post(`/api/prop-items/${propItemId}/units`, p));
export const useUpdateUnit = (propItemId: string) =>
  useUnitMutation(propItemId, ({ id, payload }: { id: string; payload: unknown }) => api.patch<PropUnit>(`/api/prop-units/${id}`, payload));
export const useDeleteUnit = (propItemId: string) =>
  useUnitMutation(propItemId, (id: string) => api.del(`/api/prop-units/${id}`));
export const useSetItemVersion = (propItemId: string) =>
  useUnitMutation(propItemId, (version: string) => api.patch(`/api/prop-items/${propItemId}`, { version }));

export type LabelSize = 'mini' | 'small' | 'std';
export const LABEL_SIZES: { value: LabelSize; label: string; hint: string }[] = [
  { value: 'mini', label: 'Mini', hint: '~25×13 mm — jen QR + číslo' },
  { value: 'small', label: 'Malé', hint: '~38×19 mm — QR + název + číslo + verze' },
  { value: 'std', label: 'Detailní', hint: '~62×32 mm — vše vč. výrobce a kontaktu' },
];
export const labelsPdfUrl = (propItemId: string, size: LabelSize = 'small') => `/api/prop-items/${propItemId}/labels.pdf?size=${size}`;
export const unitLabelPdfUrl = (unitId: string, size: LabelSize = 'small') => `/api/prop-units/${unitId}/label.pdf?size=${size}`;

export const UNIT_STATUS_META: Record<PropUnitStatus, { label: string; badge: string }> = {
  ok: { label: 'V pořádku', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  in_repair: { label: 'V opravě', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  retired: { label: 'Vyřazeno', badge: 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400 line-through' },
};

// --- Veřejná stránka kusu (z QR) ---
export interface PublicUnit {
  studioName: string;
  studioWeb: string | null;
  studioEmail: string | null;
  name: string;
  version: string | null;
  serial: string;
  total: number;
  monthYear: string;
  status: PropUnitStatus;
}
export function usePublicUnit(token: string) {
  return useQuery({ queryKey: ['unit-public', token], queryFn: () => api.get<PublicUnit>(`/api/portal/unit/${token}`), retry: false });
}

export interface UnitDetail {
  id: string;
  serialLabel: string;
  version: string | null;
  status: PropUnitStatus;
  propItem: { id: string; name: string; project: { code: string } };
  repairs: RepairLog[];
}
export function useUnitDetail(token: string) {
  return useQuery({ queryKey: ['unit-detail', token], queryFn: () => api.get<UnitDetail>(`/api/prop-units/by-token/${token}`), retry: false });
}
export function useAddRepair(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { description: string; performedBy?: string; cost?: number }) => api.post<RepairLog>(`/api/prop-units/by-token/${token}/repairs`, p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['unit-detail', token] }),
  });
}
export function useDeleteRepair(token: string) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => api.del(`/api/repair-logs/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['unit-detail', token] }) });
}
